import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../shared/hooks/useAuth'
import { profileQueryKey } from '../shared/hooks/useProfile'
import { apiClient } from '../shared/api/client'
import { ProtectedRoute } from '../shared/components/ProtectedRoute'
import { ErrorBoundary } from '../shared/components/ErrorBoundary'
import { Splash } from '../pages/Splash'
import { Login } from '../pages/Login'
import { Onboarding } from '../pages/Onboarding'
import { ProfileSetup } from '../pages/ProfileSetup'
import { Dashboard } from '../pages/Dashboard'
import { Feed } from '../pages/Feed'
import { FeedReview } from '../pages/FeedReview'
import { Matches } from '../pages/Matches'
import { Chat } from '../pages/Chat'
import { Settings } from '../pages/Settings'
import { ProfilePreview } from '../pages/ProfilePreview'
import { bridge } from '../shared/bridge'
import { AdminLogin } from '../admin/AdminLogin'
import { AdminEntry } from '../admin/AdminEntry'
import { AdminDashboard } from '../admin/AdminDashboard'
import { AdminUsers } from '../admin/AdminUsers'

function RootRouter() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { isLoading, user, profile, error, initialize } = useAuth()

  useEffect(() => {
    initialize()
  }, [initialize])

  useEffect(() => {
    if (!user?.id || !profile) {
      return
    }
    queryClient.prefetchQuery({
      queryKey: profileQueryKey(user.id),
      queryFn: async () => apiClient.get<unknown>('/api/profile/me'),
    })
  }, [user?.id, profile, queryClient])

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      return
    }
    if (import.meta.env.DEV) {
      console.log('[Router] Platform info:', {
        platform: bridge.platform,
        version: bridge.version,
        isLoading,
        user: user?.id,
        hasProfile: !!profile,
        error,
        webappAvailable: !!(typeof window !== 'undefined' && (window as any).WebApp),
      })
    }

    if (isLoading) {
      return
    }

    if (error) {
      console.error('[Router] Auth error:', error)
      // Show error state - navigate to splash and let user see error or retry
      navigate('/', { replace: true })
      return
    }

    if (!user) {
      // Check if we're in МАКС or need phone login
      const isInMaxApp = bridge.version !== '0.0.0' && bridge.platform !== 'web'
      const webappAvailable = typeof window !== 'undefined' && !!(window as any).WebApp

      if (import.meta.env.DEV) {
        console.log('[Router] User not found, routing decision:', {
          isInMaxApp,
          webappAvailable,
          version: bridge.version,
          platform: bridge.platform,
        })
      }

      if (isInMaxApp || webappAvailable) {
        navigate('/', { replace: true })
      } else {
        navigate('/login', { replace: true })
      }
      return
    }

    if (!profile) {
      // Все подмаршруты /settings (в т.ч. /settings/preview), иначе клик «Как меня видят» уводит на онбординг.
      const isAllowedNoProfileRoute =
        location.pathname === '/onboarding' ||
        location.pathname === '/profile-setup' ||
        location.pathname.startsWith('/settings')
      if (isAllowedNoProfileRoute) {
        return
      }
      if (import.meta.env.DEV) {
        console.log('[Router] Routing to onboarding (no profile)')
      }
      navigate('/onboarding', { replace: true })
      return
    }

    const isAllowedWithProfileRoute =
      location.pathname === '/dashboard' ||
      location.pathname === '/feed' ||
      location.pathname === '/matches' ||
      location.pathname.startsWith('/matches/') ||
      location.pathname.startsWith('/settings') ||
      location.pathname === '/profile-setup' ||
      location.pathname.startsWith('/review/')
    if (isAllowedWithProfileRoute) {
      return
    }

    if (import.meta.env.DEV) {
      console.log('[Router] Routing to dashboard (authenticated)')
    }
    navigate('/dashboard', { replace: true })
  }, [isLoading, user, profile, error, navigate, location.pathname])

  return (
    <Routes>
      <Route
        path="/admin/login"
        element={
          <ErrorBoundary>
            <AdminLogin />
          </ErrorBoundary>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ErrorBoundary>
            <AdminDashboard />
          </ErrorBoundary>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ErrorBoundary>
            <AdminUsers />
          </ErrorBoundary>
        }
      />
      <Route
        path="/admin"
        element={
          <ErrorBoundary>
            <AdminEntry />
          </ErrorBoundary>
        }
      />
      <Route
        path="/"
        element={
          <ErrorBoundary>
            <Splash />
          </ErrorBoundary>
        }
      />
      <Route
        path="/login"
        element={
          <ErrorBoundary>
            <Login />
          </ErrorBoundary>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/profile-setup"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <ProfileSetup />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/feed"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <Feed />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/review/skipped"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <FeedReview kind="skipped" />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/review/liked"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <FeedReview kind="liked" />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/matches"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <Matches />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/matches/:matchId"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/settings/preview"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <ProfilePreview />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route
        path="/settings"
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export function Router() {
  return (
    <BrowserRouter>
      <RootRouter />
    </BrowserRouter>
  )
}
